// eslint-disable-next-line import/named
import { addV6DebugInformation, clearAllV6DebugInformation } from './v6-debug-information.js';
import {
  applyV6PricesToQuoteItemPriceWithSupplierAdjustmentCorrections,
  convertV6CommitDetails,
  fetchQuoteAsset,
  getQuoteFrameVersion,
  getV6QuoteItemForContainer,
  isSupplierUsingSSI,
  setProviderData
} from '../data/v6/helper-functions.js';

import {
  AttachedBlobAsset,
  InputUpdateQuoteItems,
  QuoteItemPrice,
  QuoteItemState,
  QuoteItemType
} from '../../api/dealer-api-interface-quote.js';
import {
  compareFrames,
  CompareReport,
  QuoteAttributeDefaultDataProvider,
  QuoteFrameDataProvider,
  v6FrameUserMode,
  V6PickerFamilyButton,
  V6QuoteFrameEditor,
  V6QuoteFrameEditorData
} from '../../../webmodule-common/interop/webmodule-interop.js';
import { customElement } from 'lit/decorators.js';
import { DataBinding, getInternalId } from '../../../webmodule-common/other/ui/databinding/databinding.js';
import { DataTracker, FieldType } from '../../../webmodule-common/other/ui/databinding/data-tracker.js';
import { DevelopmentError, showDevelopmentError } from '../../../webmodule-common/other/development-error.js';
import { emptyGuid, newGuid } from '../../../webmodule-common/other/api/guid.js';
import { EventTemplate, Snippet } from '../../../webmodule-common/other/ui/events.js';
import { firstValidString, isEmptyOrSpace } from '../../../webmodule-common/other/ui/string-helper-functions.js';
import { FormInputAssistant } from '../../../webmodule-common/other/ui/templateresult/form-input-assistant.js';
import { FormInputAssistantBase } from '../../../webmodule-common/other/ui/templateresult/form-input-assistant-base.js';
import { getCommentEditModal } from '../ui/quote-item-comment-modal';
import { getQuoteItemTab } from '../data/quote-helper-functions.js';
import { getQuoteSuppliers } from '../quote-service.js';
import { html, TemplateResult } from 'lit';
import { information } from '../../../webmodule-common/other/ui/modal-option.js';
import { nowServer } from '../../../webmodule-common/other/datetime-converter.js';
import { offline } from '../../../webmodule-common/other/ui/offline.js';
import { QuoteItemContainer } from '../data/quote-item-container.js';
import { QuoteItemView, QuoteItemViewOptions } from '../views/quote-item-view.js';
import { quoteSupplierProvider } from '../data/quoteSupplierProvider.js';
import { StockLookupView } from '../../api/dealer-api-interface-franchisee.js';
import { supplierQuoteItemContentType } from '../data/supplier-quote-item-content-type.js';

import { glossary } from '../../../localization/global.js';
import { isDeletedQuoteItemState } from '../data/quote-container.js';
import { LangQuoteDetailView } from '../../../localization/scripts/quotes/views/quote-detail-view-lang';
import { LangV6QuoteItemFrameView } from '../../../localization/scripts/quotes/v6/v6-quote-item-frame-view-lang';
import { sleep } from '../../../webmodule-common/other/general/time.js';
import { toJsonStr } from '../../../webmodule-common/other/blob-converters.js';
import { userDataStore } from '../../dealer-franchisee/common/current-user-data-store';
import { userSecurity } from '../../../webmodule-common/other/api/user-security.js';
import { v6BootLoaderWithFailMsg } from '../../v6config/v6config.js';
import { v6Editors, v6Util } from '../../../webmodule-common/interop/interfaces/v6-config.js';

import { waitPatientlySavingWithLock } from '../../../webmodule-common/other/ui/modal-saving.js';

enum V6QuoteItemSaveState {
  nothing = 0,
  prepareForSave = 10,
  communicateWithSupplierSystem = 20,
  getBuyInCosts = 25,
  updatePricing = 28,
  CollectQuoteContract = 40,
  RequestSSI = 50,
  UpdateLocalSSI = 60,
  UpdateAllQuoteItems = 70,
  UpdateComplete = 80
}

@customElement('wm-quoteitemframeviewforv6')
export class QuoteItemFrameViewForV6 extends QuoteItemView implements QuoteFrameDataProvider {
  v6Editor: V6QuoteFrameEditor | null = null;
  dataTracker: DataTracker;
  framePicker?: V6PickerFamilyButton;
  private supplierOffline = false;
  diffReport: {
    report: CompareReport | null;
    markdown: string;
  } | null;
  commitAssets: { [key: string]: AttachedBlobAsset };

  constructor(options: QuoteItemViewOptions) {
    super(options);

    this.dataTracker = new DataTracker(new DataBinding(this.ui, getInternalId()));
    const addField = (
      fieldName: string,
      propertyType?: FieldType,
      nullable?: boolean,
      editorFieldName?: string,
      data?: () => any
    ) => {
      this.dataTracker.addObjectBinding(
        data ?? (() => this.quoteItemContainer?.item),
        fieldName,
        editorFieldName ?? fieldName,
        propertyType ?? FieldType.string,
        nullable ?? false
      );
    };

    addField('title');
    addField('description');

    this.dataTracker.addDynamic(
      'comment',
      FieldType.string,
      () => {
        return this.quoteItemContainer?.item?.comment;
      },
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      () => {},
      { nullable: true }
    );
    this.dataTracker.addDynamic(
      'commentToManufacturer',
      FieldType.string,
      () => {
        return this.quoteItemContainer?.item?.commentToManufacturer;
      },
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      () => {},
      { nullable: true }
    );

    // Changed nullable to true since we are checking the field in the validation.
    addField('quantity', FieldType.int, true);
    addField('lastModifiedDate', FieldType.dateTime, true);
  }

  //only used for new frames
  private _id: string = newGuid();

  get id(): string {
    return this.quoteItemContainer?.item.id ?? this._id;
  }

  get quoteAttributeDefaultDataProvider(): QuoteAttributeDefaultDataProvider | undefined {
    return this;
  }
  getBaseLineQuoteDefaults(): Promise<unknown | undefined> {
    return Promise.resolve(undefined);
  }
  get supplierReferenceOverrideKey(): string {
    //the quote owner is the branch ID, which is what we are tying our v6 customer to
    return this.quoteManager.quote.quoteOwnerId;
  }

  get readonly(): boolean {
    return this.isDataReadonly() || this.supplierOffline;
  }

  get quoteItem() {
    if (!this.quoteItemContainer) return undefined;
    return getV6QuoteItemForContainer(this.quoteItemContainer);
  }

  get quoteItemVersion(): string | undefined {
    if (!this.quoteItemContainer) return undefined;
    return getQuoteFrameVersion(this.quoteItemContainer);
  }

  get quoteIGUs() {
    return this.getQuoteIGUs();
  }

  get userMode(): string | undefined {
    if (userSecurity().isPowerUser()) return v6FrameUserMode.powerUser;
    else return v6FrameUserMode.standardUser;
  }

  get quantity(): number {
    if (!this.quoteItemContainer) return 1;
    try {
      const uiValue = Math.max(1, this.getQuantityFromUI());
      return uiValue;
    } catch {
      return this.quoteItemContainer?.item.quantity ?? 1;
    }
  }

  get thumbnailURL() {
    const quoteItem = this.quoteItemContainer?.item;
    return this.quoteApi.convertQuoteItemAssetPathToUrl(
      quoteItem?.quoteId,
      quoteItem?.id,
      quoteItem?.virtualThumbnailPath
    );
  }

  get quoteDefaults() {
    return this.getQuoteDefaults();
  }

  //QuoteFrameDataProvider
  async dispose(): Promise<void> {
    await super.dispose();
    if (!this.readonly) this.v6Editor?.releaseResources();
    this.v6Editor = null;
    await clearAllV6DebugInformation('QuoteItemFrameViewForV6');
  }

  /**
   * inherited
   * @returns
   */
  public isTab(): boolean {
    return true;
  }

  /**
   * inherited
   * @returns
   */
  public async prepareForSave(): Promise<void> {
    await this.setQuoteItemSaveState(V6QuoteItemSaveState.prepareForSave);
    if (this.readonly) return;
    if (!this.v6Editor) return;
    if (this.quoteItemContainer) {
      if (this.dataTracker.modified) this.dataTracker.applyChangeToValue();

      //the provider data is a base64 encoding of the v6configuration data
      //normally we only want to assign this data when we hit the save button
      //for now, we are updating the quoteitem every single call back so that
      //it stress tests the system
      //TODO-we dont need to adjust this every single time normally, only when
      if (!this.quoteItemContainer.data)
        throw new DevelopmentError('v6-quote-item-frame-view, quoteItemContainer.data is null');

      //design changes now mean that we always need to trigger the server call to update buyin items, pricing, and server side macro
      //events. we can't assume nothing has changed.
      await this.setQuoteItemSaveState(V6QuoteItemSaveState.communicateWithSupplierSystem);
      let details: V6QuoteFrameEditorData | null = null;
      try {
        if (this.v6Editor.changed || this.quoteManager.changedItemQuantity(this.quoteItemContainer?.item.id))
          details = await this.v6Editor.commit();
      } catch {
        await offline();
      }
      if (details) {
        const buyIn = details?.buyInItems;
        const price = details.price;
        this.commitAssets = convertV6CommitDetails(details);

        const originalFrameData = this.quoteItem ? v6Util().getFrameData(this.quoteItem as any) : null;
        const newFrameData = details.quoteItem ? v6Util().getFrameData(details.quoteItem as any) : null;
        const frameDiff = compareFrames(originalFrameData, newFrameData, { excludeHidden: true });
        this.quoteItemContainer.differenceReport = frameDiff;
        if (buyIn && buyIn.length > 0) await this.setQuoteItemSaveState(V6QuoteItemSaveState.getBuyInCosts);
        const buyInCosts = await this.quoteManager.getBuyInCosts(buyIn);
        const buyInMetaData = this.buyInDataJsonStr(buyInCosts);

        this.quoteItemContainer.item.isRestrictedToPowerUser = details.requiredUserMode === v6FrameUserMode.powerUser;
        await this.setQuoteItemSaveState(V6QuoteItemSaveState.updatePricing);

        applyV6PricesToQuoteItemPriceWithSupplierAdjustmentCorrections(
          this.quoteItemContainer.item.quantity,
          price,
          this.quoteItemContainer.price,
          buyInCosts
        );

        this.quoteItemContainer.buyInData = buyInMetaData ?? undefined;
        // Set providerData last - this also flushes the clientSideCache
        setProviderData(this.quoteItemContainer, this.commitAssets['data']?.data ?? null);
      }
    }
  }
  public async fetchAsset(assetType: string): Promise<string | object | null> {
    if (!this.quoteItemContainer) return null;
    const newPath = this.quoteItemContainer.data?.assetVirtualPaths?.[assetType]?.path ?? '';
    const publicUrl = this.quoteItemContainer.data?.assetVirtualPaths?.[assetType]?.publicUrl ?? null;
    const v6Data = getV6QuoteItemForContainer(this.quoteItemContainer) as any;

    switch (assetType) {
      case 'model3d':
        return (
          v6Data.vrml ??
          (await fetchQuoteAsset({
            quoteId: this.quoteItemContainer.item.quoteId,
            quoteItemId: this.quoteItemContainer.item.id,
            assetPath: newPath,
            publicUrl: publicUrl
          }))
        ); // Handle 3D model asset
        break;
      case 'bomView':
        v6Data.bomView ??
          (await fetchQuoteAsset({
            quoteId: this.quoteItemContainer.item.quoteId,
            quoteItemId: this.quoteItemContainer.item.id,
            assetPath: newPath,
            publicUrl: publicUrl
          }));
        break;
      case 'thumbnail':
        return null; //notsupported
        break;
      case 'data':
        return v6Data;
        break;
      default:
        return null;
    }
    return null;
  }

  public async prepareEditor(): Promise<void> {
    try {
      if (await v6BootLoaderWithFailMsg()) {
        if (!this.readonly) {
          const suppliers = await getQuoteSuppliers();
          this.supplierOffline = !(
            suppliers.find(x => x.supplierId === this.quoteManager.quote.supplierId)?.online ?? false
          );
          if (this.supplierOffline) await information(LangV6QuoteItemFrameView.supplierOfflineReadonly());
        }
        await addV6DebugInformation('QuoteItemFrameViewForV6', {
          quoteItemId: this._id,
          quoteItemPosition: this.quoteManager.itemPosition(this._id, true)?.toString() ?? '',
          quoteItemTitle: 'New Item'
        });
        this.v6Editor = await v6Editors().getQuoteFrameEditorService(this);
        if (this.v6Editor) {
          this.v6Editor.ui.addEventListener('v6frameview', e => {
            const ev: CustomEvent = e as CustomEvent;
            if (ev.detail.error && ev.detail.shutdownRequired) this.abandonAndClose(true);
          });
        }
      }
      this._readyToEdit = this.v6Editor != null;
      if (this.v6Editor && !this.quoteItemContainer) {
        this._readyToEdit = false;
        let details: V6QuoteFrameEditorData | null = null;
        try {
          details = await this.v6Editor.commit();
        } catch {
          await offline();
          return;
        }
        if (details) {
          const price = details.price;
          const assets = convertV6CommitDetails(details);

          const providerType = quoteSupplierProvider.v6;
          const providerReferenceId: string = emptyGuid;

          const buyIn = details.buyInItems;
          const buyInCosts = await this.quoteManager.getBuyInCosts(buyIn);
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { quoteItemCommonId, currency, ...priceSourceData } = price;
          const id = this._id;
          const date = nowServer();

          //create mock price object so we can use the same
          //code for generating the price
          const qprice: QuoteItemPrice = {
            breakdowns: null,
            calculatedGrossSellingPrice: 0,
            calculatedNetSellingPrice: 0,
            dateCreated: date,
            id: id,
            percentMarginOrMarkup: 0,
            priceAdjustment: 0,
            quantityCost: 0,
            recordVersion: '',
            requiresRepricing: false,
            singleUnitCost: 0,
            sourceData: priceSourceData,
            supplierGrossQuantityCost: 0,
            supplierGrossSingleUnitCost: 0,
            supplierNettQuantityCost: 0,
            supplierNettSingleUnitCost: 0,
            supplierPriceAdjustment: 0,
            isTaxableItem: true,
            supplierPriceAdjustmentEnabled: false,
            supplierPriceAdjustmentReason: null
          };
          applyV6PricesToQuoteItemPriceWithSupplierAdjustmentCorrections(1, price, qprice, buyInCosts);

          const buyInMetaData = this.buyInDataJsonStr(buyInCosts);
          //we need to populate the dataprovider correctly because SSI processing uses the real embedded price information.
          const exInput: QuoteItemContainer = {
            data: {
              assetVirtualPaths: {},
              dateCreated: date,
              lastModifiedDate: date,
              lastModifiedUserId: emptyGuid,
              recordVersion: '',
              id: id,
              providerData: assets['data']?.data ?? null
            },

            buyInData: buyInMetaData ?? undefined,
            item: {
              comment: null,
              commentToManufacturer: null,
              commonId: id,
              dateCreated: date,
              description: this.v6Editor.frameDescription,
              id: id,
              isRestrictedToPowerUser: details.requiredUserMode === v6FrameUserMode.powerUser,
              itemType: QuoteItemType.Provider,
              lastModifiedDate: date,
              lastModifiedUserId: emptyGuid,
              providerReferenceId: providerReferenceId,
              serviceProvider: providerType,
              quantity: 1,
              quoteId: this.quoteManager.quoteId,
              quoteItemContentType: supplierQuoteItemContentType.CID_FRAM,
              recordVersion: '',
              title: LangV6QuoteItemFrameView.frameTitle(),
              virtualThumbnailPath: '',
              isShippingItem: false,

              state: QuoteItemState.Active
            },
            price: qprice
          };

          const ssiProcessor = this.quoteManager.ssiProcessor(undefined, [exInput]);
          const ssiInputs = await ssiProcessor.processSSI();

          this.quoteItemContainer = await this.quoteManager.createQuoteItemEx(exInput, assets, ssiInputs);
        }
        this._readyToEdit = this.quoteItemContainer != null;
      }
      if (this._readyToEdit && this.quoteItemContainer) {
        await addV6DebugInformation('QuoteItemFrameViewForV6', {
          quoteItemId: this.quoteItemContainer.item.id,
          quoteItemPosition: this.quoteManager.itemPosition(this.quoteItemContainer.item.id)?.toString() ?? '',
          quoteTitle: this.quoteItemContainer.item.title,
          quoteItemDescription: this.quoteItemContainer.item.description
        });
      }
    } catch (e) {
      await showDevelopmentError(e as Error);
      this._readyToEdit = false;
    }
  }

  private createCommentButton(fieldName: string, forms: FormInputAssistant): { label: string; readonly: boolean } {
    const currentValue = this.dataTracker.getObjectValue(fieldName) as string;

    if (forms.forceReadonly) {
      return {
        label: LangV6QuoteItemFrameView.viewNote(),
        readonly: !currentValue || isEmptyOrSpace(currentValue)
      };
    }

    return {
      label: isEmptyOrSpace(currentValue) ? LangV6QuoteItemFrameView.addNote() : LangV6QuoteItemFrameView.editNote(),
      readonly: false
    };
  }

  private async handleCommentEdit(fieldName: string, title: string, forms: FormInputAssistant): Promise<void> {
    if (!this.quoteItemContainer) return;
    if (fieldName !== 'commentToManufacturer' && fieldName !== 'comment') return;

    const currentValue = this.dataTracker.getObjectValue(fieldName) as string;
    const modal = await getCommentEditModal(undefined, title, forms.forceReadonly, currentValue);

    if (modal.result === 'update') {
      if (fieldName === 'comment') {
        this.quoteItemContainer.item.comment = modal.comment ?? null;
      } else if (fieldName === 'commentToManufacturer') {
        this.quoteItemContainer.item.commentToManufacturer = modal.comment ?? null;
      }

      this.v6Editor?.requestUpdate();
    }
  }

  private createCommentRow(
    forms: FormInputAssistantBase,
    title: string,
    tooltip: string,
    buttonConfig: { label: string; readonly: boolean },
    onClick: () => void
  ): TemplateResult {
    return forms.multiEditRow({
      title,
      toolTip: tooltip,
      slotted: forms.buttonPrimary(buttonConfig.label, {
        events: { click: onClick },
        readonly: buttonConfig.readonly
      })
    });
  }

  propertiesTemplate(): TemplateResult {
    const forms = new FormInputAssistant(this.dataTracker, this.quoteManager.isReadonly());
    const formsNonForced = new FormInputAssistantBase(false);

    if (!this.quoteItemContainer?.item) {
      return html``;
    }

    const customerNoteButton = this.createCommentButton('comment', forms);
    const manufacturerNoteButton = this.createCommentButton('commentToManufacturer', forms);

    const handleCustomerNote = () => this.handleCommentEdit('comment', LangV6QuoteItemFrameView.notesToClient(), forms);

    const handleManufacturerNote = () =>
      this.handleCommentEdit('commentToManufacturer', LangV6QuoteItemFrameView.notesToManufacturer(), forms);

    return html`
      <div class="form-one-col quoteItemFrameProperties">
        <div class="row attributeContents">
          <div class="v6config-group">
            <div class="v6config-group-header">${LangV6QuoteItemFrameView.propertiesSectionTitle()}</div>
            ${forms.text('title', LangV6QuoteItemFrameView.frameTitle(), 120)}
            ${forms.text('description', LangV6QuoteItemFrameView.description(), 120)}
            ${forms.int('quantity', LangV6QuoteItemFrameView.quantity())}
            ${this.createCommentRow(
              formsNonForced,
              LangV6QuoteItemFrameView.notesToClient(),
              LangV6QuoteItemFrameView.notesToClientTooltip(),
              customerNoteButton,
              handleCustomerNote
            )}
            ${this.createCommentRow(
              formsNonForced,
              LangV6QuoteItemFrameView.notesToManufacturer(),
              LangV6QuoteItemFrameView.notesToManufacturerTooltip(),
              manufacturerNoteButton,
              handleManufacturerNote
            )}
            ${userDataStore.isFabricatorMode
              ? forms.dateReadonly('lastModifiedDate', LangQuoteDetailView.lastModified())
              : html``}
          </div>
        </div>
      </div>
    `;
  }

  public internalDataChanged(): boolean {
    return (
      !this.readonly &&
      (this.quoteManager.changedItem(this.quoteItemContainer?.item.id) ||
        this.quoteManager.quoteChanged() ||
        (this.v6Editor?.changed ?? false))
    );
  }

  displaySaveModal(): boolean {
    return true;
  }

  public allowDeletePage(): boolean {
    return true;
  }

  discardLabel(): unknown {
    return LangV6QuoteItemFrameView.discardFrameChanges();
  }

  buttonMenu(): Snippet {
    return html`${this.abandonAndCloseButton()}`;
  }

  getDataDictionaryName(): string {
    return glossary('frame');
  }

  public isDataReadonly(): boolean {
    if (!this.quoteItemContainer) return true;
    return this.quoteManager.isReadonly() || isDeletedQuoteItemState(this.quoteItemContainer.item);
  }

  protected revertChanges(): Promise<boolean> {
    if (this.quoteItemContainer) {
      const backup = this.quoteManager.quoteItemContainer(this.quoteItemContainer.item.id, true);
      this.quoteItemContainer.item.title = backup.item.title;
      this.quoteItemContainer.item.description = backup.item.description;
      this.quoteItemContainer.item.quantity = backup.item.quantity;
      this.quoteItemContainer.item.comment = backup.item.comment;
    }
    return Promise.resolve(true);
  }

  protected getValidationErrors(): string[] {
    const errors: string[] = [];
    const quantity = this.getQuantityFromUI();
    if (isNaN(quantity) || quantity < 1) errors.push(LangV6QuoteItemFrameView.invalidQuantity({ quantity }));

    return errors;
  }

  protected async internalSaveData(): Promise<boolean> {
    //TODO- At this point, we have done everything needed to save the frame item. now we need to update the entire quote.
    let updateItems: InputUpdateQuoteItems | undefined = undefined;
    if (isSupplierUsingSSI(this.quoteManager.quote.supplierId)) {
      await this.setQuoteItemSaveState(V6QuoteItemSaveState.RequestSSI);
      const ssi = this.quoteManager.ssiProcessor();
      updateItems = await ssi.processSSI();

      await this.setQuoteItemSaveState(V6QuoteItemSaveState.UpdateLocalSSI);
    }

    await this.setQuoteItemSaveState(V6QuoteItemSaveState.UpdateAllQuoteItems);
    try {
      if (this.quoteItemContainer) {
        this.quoteItemContainer = await this.quoteManager.saveAndUpdateQuoteItem(
          this.quoteItemContainer,
          this.commitAssets,
          updateItems
        );
        return this.quoteManager.lastSaveSuccessful;
      }

      return false;
    } finally {
      await this.setQuoteItemSaveState(V6QuoteItemSaveState.UpdateComplete);
    }
  }

  protected getCaption(): Snippet {
    return getQuoteItemTab(
      firstValidString(
        this.quoteItemContainer?.item.title,
        this.v6Editor?.frameDescription,

        LangV6QuoteItemFrameView.newFrame()
      ),
      this.quoteManager.itemPosition(this.quoteItemContainer?.item.id)
    );
  }

  protected bodyTemplate(): EventTemplate {
    if (this.v6Editor) {
      return html`${this.v6Editor.ui}`;
    } else return html``;
  }

  protected getQuoteIGUs(): unknown {
    throw new DevelopmentError('getQuoteIGUs() must override');
  }

  saveQuoteDefaults(_quoteDefaultsData: unknown): boolean {
    throw new DevelopmentError('saveQuoteDefaults() must override');
  }

  saveAttributeUpdatedHistory(_quoteAttributeHistoryData: unknown): boolean {
    throw new DevelopmentError('saveAttributeUpdatedHistory() must override');
  }

  getAttributeUpdatedHistory(): unknown {
    throw new DevelopmentError('getAttributeUpdatedHistory() must override');
  }

  getQuoteDefaults(): unknown {
    throw new DevelopmentError('getQuoteDefaults() must override');
  }

  getGlobalAttributesMasterSet(): unknown {
    throw new DevelopmentError('getGlobalAttributesMasterSet() must override');
  }

  saveGlobalAttributesMasterSet(_data: unknown): boolean {
    throw new DevelopmentError('getGlobalAttributesMasterSet() must override');
  }
  private async v6BusyCheck() {
    if (this.v6Editor) {
      const awaiter = (this.v6Editor as any).updateComplete as Promise<void> | null | undefined;
      if (awaiter !== undefined) await awaiter;
      else {
        await sleep(20);
        while (this.v6Editor?.busy) {
          await sleep(20);
        }
      }
    }
  }

  protected async readyToClose(): Promise<boolean> {
    await waitPatientlySavingWithLock({ action: this.v6BusyCheck() });
    return true;
  }

  private async setQuoteItemSaveState(state: V6QuoteItemSaveState) {
    let info = '';
    const qic = this.quoteItemContainer;
    const qi = qic?.item;
    if (!qi) {
      await this.informationDispatcher.setInformation('');
      return;
    }
    switch (state) {
      case V6QuoteItemSaveState.nothing:
        info = LangV6QuoteItemFrameView.editingFrame({ title: qi.title });
        break;
      case V6QuoteItemSaveState.prepareForSave:
        info = LangV6QuoteItemFrameView.preparingToSave({ title: qi.title });
        break;
      case V6QuoteItemSaveState.communicateWithSupplierSystem:
        info = LangV6QuoteItemFrameView.preparingToSaveWithSupplier({ title: qi.title });
        break;
      case V6QuoteItemSaveState.CollectQuoteContract:
        info = LangV6QuoteItemFrameView.prepareInformationForSupplier({ title: qi.title });
        break;
      case V6QuoteItemSaveState.updatePricing:
        info = LangV6QuoteItemFrameView.updatingQuotePrice({ title: qi.title });
        break;
      case V6QuoteItemSaveState.RequestSSI:
        info = LangV6QuoteItemFrameView.requestingUpdatesFromSupplier({ title: qi.title });
        break;
      case V6QuoteItemSaveState.UpdateLocalSSI:
        info = LangV6QuoteItemFrameView.updatingQuoteWithSupplierChanges({ title: qi.title });
        break;
      case V6QuoteItemSaveState.UpdateAllQuoteItems:
        info = LangV6QuoteItemFrameView.savingSendingUpdates({ title: qi.title });
        break;
      case V6QuoteItemSaveState.UpdateComplete:
        info = LangV6QuoteItemFrameView.savingCompleted({ title: qi.title });
        break;
    }
    await this.informationDispatcher.setInformation(info);
  }

  private getQuantityFromUI(): number {
    return this.dataTracker.getEditorValue('quantity') as number;
  }

  private buyInDataJsonStr(buyInCosts?: StockLookupView[]): string | undefined {
    if (buyInCosts && buyInCosts.length > 0) {
      return toJsonStr(buyInCosts) ?? undefined;
    } else return undefined;
  }
}
